import { hasAtLeastRole, Role, toRole } from './role.js';
const CAPABILITY_TABLE = {
    // --- Bookie: desk staff -------------------------------------------------
    /** Put an order in someone else's name (cart.meta.orderOnBehalfOf). */
    bookOnBehalf: { role: Role.bookie },
    /** Complete an order without paying ('admin' provider). Role from the site. */
    payOutsideSystem: { setting: 'payOutsideSystemRole' },
    /** Book a closed, club-locked, group-locked or over-quota slot. */
    bookLockedSlot: { role: Role.bookie },
    /** Book while Booking er åpen is off. */
    bookWhenClosed: { role: Role.bookie },
    /** Sign up outside the signup window or into a group-restricted sub-event. */
    eventSignupBypass: { role: Role.bookie },
    /** See and export event participants. */
    readEventParticipants: { role: Role.bookie },
    /** Release or change other people's tickets. */
    releaseOthersTickets: { role: Role.bookie },
    /** Close and reopen one slot, and release a fixed time once. */
    calendarOverrideState: { role: Role.bookie },
    /** Look an order up at the desk. */
    readOrders: { role: Role.bookie },
    /** The admin calendar, with booker details. */
    readAdminCalendar: { role: Role.bookie },
    // --- Admin: everything under Admin --------------------------------------
    /** Change one slot's price, activity or reference. */
    calendarOverridePrice: { role: Role.admin },
    /** Open the Admin area at all. */
    adminArea: { role: Role.admin },
    /** Write the site document (settings, slot definitions, policies, ...). */
    editSite: { role: Role.admin },
    editEvents: { role: Role.admin },
    editPages: { role: Role.admin },
    /** The mail outbox (receipts, codes, reset links). */
    readOutbox: { role: Role.admin },
    readIncidents: { role: Role.admin },
    /** Customer records (shadow users: name, email, phone). */
    readCustomers: { role: Role.admin },
    /** Payment setup. Admins keep it (ADR 0015, not owner-only). */
    managePayment: { role: Role.admin },
    /** Switch modules. Admins keep it (ADR 0015, not owner-only). */
    manageModules: { role: Role.admin },
    manageInvoices: { role: Role.admin },
    // --- Owner ---------------------------------------------------------------
    /** The only thing an admin may not do. */
    changeRoles: { role: Role.owner }
};
const Capabilities = Object.keys(CAPABILITY_TABLE);
/**
 * The stored value of **Tillat betaling utenfor systemet**, read leniently.
 *
 * Bookie is the floor (D122): a customer's no-pay button books through the
 * `free` provider, which rejects a paid cart, so `everyone` could never work —
 * it reads as bookie. Missing or unknown is bookie, the setting's old default.
 * `system` means "Ingen": nobody but root.
 */
const toPayOutsideSystemRole = (raw) => {
    const role = typeof raw === 'string' ? toRole(raw) : Role.bookie;
    return role === Role.everyone ? Role.bookie : role;
};
/** The lowest role that may pay outside the system on this site. */
const payOutsideSystemRole = (siteData) => toPayOutsideSystemRole(siteData.settings.payOutsideSystemRole);
const settingRole = (setting, siteData) => {
    switch (setting) {
        case 'payOutsideSystemRole':
            return payOutsideSystemRole(siteData);
    }
};
/**
 * May `role` do `capability` on this site?
 *
 * `siteData` is needed only for a capability whose role comes from a site
 * setting; without it such a capability is denied. Unknown capabilities are
 * denied too, even to root.
 */
const can = (role, capability, siteData) => {
    const threshold = CAPABILITY_TABLE[capability];
    if (!threshold)
        return false;
    if ('role' in threshold)
        return hasAtLeastRole(role, threshold.role);
    if (!siteData)
        return false;
    return hasAtLeastRole(role, settingRole(threshold.setting, siteData));
};
/**
 * A user's role on one site, from their `sites/<site>/admin/<uid>` document
 * and whether they are root (`admins/<uid>` exists).
 *
 * Missing document or unknown role → everyone; the legacy 'support' → bookie;
 * root → the highest role, on every site, whatever the site document says.
 */
const resolveSiteRole = (adminDoc, isRoot) => {
    if (isRoot)
        return Role.system;
    if (!adminDoc || typeof adminDoc !== 'object')
        return Role.everyone;
    const role = adminDoc.role;
    return typeof role === 'string' ? toRole(role) : Role.everyone;
};
export { can, Capabilities, CAPABILITY_TABLE, payOutsideSystemRole, resolveSiteRole, toPayOutsideSystemRole };
//# sourceMappingURL=capabilities.js.map